import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import useCountries from '../../lib/hooks/useCountries';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { residenceHistorySelector } from '../../reducers/selectors';

const SecondAddressPage = ({ residenceHistory, dispatchAddPageData, dispatchWizardNav }) => {
  const residences = [...residenceHistory];
  const residenceNeedingAddressIndexes = [];
  residences.forEach((res, index) => {
    if (res.addressRequired === true && index !== 0) {
      residenceNeedingAddressIndexes.push(index);
    }
  });
  const secondResidenceNeedingAddressIndex = residenceNeedingAddressIndexes[0];
  const residenceNeedingAddress = residences[secondResidenceNeedingAddressIndex];
  const {
    countryCode,
    address: initialAddress,
    city: initialCity,
    postCode: initialPostCode,
  } = residenceNeedingAddress;

  const { data: countries } = useCountries();
  const [address, setAddress] = useState(initialAddress || '');
  const [city, setCity] = useState(initialCity || '');
  const [postCode, setPostCode] = useState(initialPostCode || '');

  const onAddressChanged = event => setAddress(event.target.value);
  const onCityChanged = event => setCity(event.target.value);
  const onPostCodeChanged = event => setPostCode(event.target.value);

  const isInvalid = address.trim() === '' || city.trim() === '' || postCode.trim() === '';

  const titleText = (code, countryList) => {
    let countryName = code;
    if (countryList.length > 0) {
      const country = countryList.find(c => c.value === code);
      countryName = country ? country.label : code;
    }
    return `What was your address when you lived in ${countryName}?`;
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'second-address');

    const updatedResidenceNeedingAddress = {
      address,
      city,
      postCode,
      ...residenceNeedingAddress,
    };
    residences[secondResidenceNeedingAddressIndex] = updatedResidenceNeedingAddress;

    const residenceHistoryPayload = payloadFactory.createAddPageDataPayload(
      'residenceHistory',
      residences,
    );
    dispatchAddPageData(residenceHistoryPayload);

    dispatchWizardNav({ dir: 'fwd' });
  };

  const helpText = `You’ve told us you recently lived in multiple countries for longer than a year. 
    We need a few more details from you to fast track finalising your loan if you choose us as your loan provider.`;

  return (
    <PageLayout title={titleText(countryCode, countries)} helpText={helpText}>
      <div className="row">
        <form className="col col--8/16-small" onSubmit={handleSubmit}>
          <div className="row-spacer">
            <InputField
              type="text"
              placeholder="Address"
              id="address"
              value={address}
              onChange={onAddressChanged}
            />
          </div>

          <div className="row-spacer">
            <InputField
              type="text"
              placeholder="City"
              id="city"
              value={city}
              onChange={onCityChanged}
            />
          </div>

          <div className="row-spacer">
            <InputField
              type="text"
              placeholder="Post code"
              id="postCode"
              value={postCode}
              onChange={onPostCodeChanged}
            />
          </div>

          <div className="row-spacer">
            <Button type="submit" isDisabled={isInvalid}>
              Next
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

SecondAddressPage.propTypes = {
  residenceHistory: PropTypes.arrayOf(
    PropTypes.shape({
      countryCode: PropTypes.string.isRequired,
      stateCode: PropTypes.string.isRequired,
      address: PropTypes.string,
      city: PropTypes.string,
      postCode: PropTypes.string,
      startMonth: PropTypes.number.isRequired,
      startYear: PropTypes.number.isRequired,
    }),
  ).isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
};

export const mapStateToProps = state => ({
  residenceHistory: residenceHistorySelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { SecondAddressPage };

export default connect(mapStateToProps, mapDispatchToProps)(SecondAddressPage);
